// API client shared by every front end. Uses only fetch, so it runs in a browser, a WebView
// (Android via Capacitor, macOS via Tauri) and Node alike.

import type { DocumentRecord, Item, Page, ScoredCandidate, SearchHit, AuditEvent } from './types.ts';
import type { Identifiers, DecodedCode } from './identify.ts';
import type { Decision } from './matching.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export interface LookupResult extends Page<ScoredCandidate> {
  /** Connectors that failed this lookup, so the UI can say what is missing instead of hiding it. */
  errors: Array<{ connectorId: string; code: string; message: string }>;
}

export interface AttachResult {
  document: DocumentRecord;
  /** True when the same bytes were already in this library. */
  deduplicated: boolean;
}

export interface StepCost { provider: string; cached: boolean; costMicroUsd: number; error?: string }

export interface Capture {
  id: string;
  status: 'matched' | 'needs_review' | 'resolved' | 'dismissed';
  createdAt: string;
  resolvedAt: string | null;
  imageSha256: string;
  identifiers: Identifiers;
  codes: DecodedCode[];
  ocrText: string;
  candidates: ScoredCandidate[];
  decision: Decision & {
    model: { choice: number | null; confidence: number; reason: string; modelId: string; inputTokens: number; outputTokens: number; costMicroUsd: number; cached: boolean } | null;
    modelNote: string | null;
    steps: { barcode: StepCost; ocr: StepCost; llm: StepCost | null; sources: { cached: number; fetched: number; errors: string[] } };
    paidMicroUsd: number;
    importError?: string;
  };
  itemId: string | null;
  documentId: string | null;
}

export interface Me {
  owner: string;
  email: string | null;
  via: 'session' | 'dev-bypass';
  llm: { model: string | null; today: { calls: number; costMicroUsd: number }; dailyBudgetMicroUsd: number };
  ocr: string;
}

export interface AuthInfo { oidcConfigured: boolean; provider: string | null; missing: string[]; devBypass: boolean }

export class ManualiumClient {
  private readonly baseUrl: string;
  private readonly token: string | null;
  private readonly fetchImpl: typeof fetch;

  /** With `token`: the dev-bypass bearer token. Without: the browser's session cookie (same origin). */
  constructor(opts: { baseUrl: string; token?: string | null; fetchImpl?: typeof fetch }) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.token = opts.token ?? null;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private headers(): Record<string, string> {
    // The CSRF header: a cross-site form or image can't set it, so cookie writes need it.
    return this.token ? { authorization: `Bearer ${this.token}`, 'x-manualium-csrf': '1' } : { 'x-manualium-csrf': '1' };
  }

  private async call<T>(method: string, path: string, body?: BodyInit | object, contentType?: string): Promise<T> {
    const headers: Record<string, string> = this.headers();
    let payload: BodyInit | undefined;
    if (body instanceof Uint8Array || body instanceof ArrayBuffer || (typeof Blob !== 'undefined' && body instanceof Blob)) {
      payload = body as BodyInit;
      headers['content-type'] = contentType ?? 'application/octet-stream';
    } else if (body !== undefined) {
      payload = JSON.stringify(body);
      headers['content-type'] = 'application/json';
    }
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, { method, headers, body: payload, credentials: 'same-origin' });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new ApiError(res.status, err.error ?? 'http_error', err.message ?? res.statusText);
    }
    return (await res.json()) as T;
  }

  createItem(input: { make: string; model: string; nickname?: string; room?: string; tags?: string[] }) {
    return this.call<Item>('POST', '/v1/items', input);
  }

  listItems(offset = 0, limit = 50) {
    return this.call<Page<Item>>('GET', `/v1/items?offset=${offset}&limit=${limit}`);
  }

  getItem(id: string) {
    return this.call<Item & { documents: DocumentRecord[] }>('GET', `/v1/items/${encodeURIComponent(id)}`);
  }

  lookup(q: { make?: string; model: string }, offset = 0, limit = 10) {
    const p = new URLSearchParams({ model: q.model, offset: String(offset), limit: String(limit) });
    if (q.make) p.set('make', q.make);
    return this.call<LookupResult>('GET', `/v1/lookups?${p}`);
  }

  attachUpload(itemId: string, bytes: Uint8Array | ArrayBuffer | Blob, filename: string) {
    const p = new URLSearchParams({ filename });
    return this.call<AttachResult>('POST', `/v1/items/${encodeURIComponent(itemId)}/documents?${p}`, bytes, 'application/pdf');
  }

  attachUrl(itemId: string, url: string) {
    return this.call<AttachResult>('POST', `/v1/items/${encodeURIComponent(itemId)}/documents`, { url });
  }

  attachCandidate(itemId: string, connectorId: string, ref: string) {
    return this.call<AttachResult>('POST', `/v1/items/${encodeURIComponent(itemId)}/documents`, { connectorId, ref });
  }

  search(q: string, offset = 0, limit = 20) {
    const p = new URLSearchParams({ q, offset: String(offset), limit: String(limit) });
    return this.call<Page<SearchHit>>('GET', `/v1/search?${p}`);
  }

  audit(offset = 0, limit = 50) {
    return this.call<Page<AuditEvent>>('GET', `/v1/audit?offset=${offset}&limit=${limit}`);
  }

  /** Decrypted manual bytes. Served same-origin with a strict CSP; render with pdf.js, never a plugin. */
  async documentContent(documentId: string): Promise<ArrayBuffer> {
    const res = await this.fetchImpl(`${this.baseUrl}/v1/documents/${encodeURIComponent(documentId)}/content`, {
      headers: this.headers(), credentials: 'same-origin',
    });
    if (!res.ok) throw new ApiError(res.status, 'content_unavailable', res.statusText);
    return res.arrayBuffer();
  }

  authInfo() {
    return this.call<AuthInfo>('GET', '/auth/config');
  }

  me() {
    return this.call<Me>('GET', '/v1/me');
  }

  logout() {
    return this.call<{ ok: boolean }>('POST', '/auth/logout');
  }

  /** Send a photo (PNG/JPEG/WebP). The server decodes codes, runs OCR, matches and may auto-attach. */
  capture(image: Uint8Array | ArrayBuffer | Blob, contentType = 'image/jpeg') {
    return this.call<Capture>('POST', '/v1/captures', image, contentType);
  }

  getCapture(id: string) {
    return this.call<Capture>('GET', `/v1/captures/${encodeURIComponent(id)}`);
  }

  listCaptures(status: Capture['status'] | null, offset = 0, limit = 20) {
    const p = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (status) p.set('status', status);
    return this.call<Page<Capture>>('GET', `/v1/captures?${p}`);
  }

  async captureImage(id: string): Promise<Blob> {
    const res = await this.fetchImpl(`${this.baseUrl}/v1/captures/${encodeURIComponent(id)}/image`, { headers: this.headers(), credentials: 'same-origin' });
    if (!res.ok) throw new ApiError(res.status, 'image_unavailable', res.statusText);
    return res.blob();
  }

  resolvePick(id: string, candidateIndex: number) {
    return this.call<Capture>('POST', `/v1/captures/${encodeURIComponent(id)}/resolve`, { candidateIndex });
  }

  resolveManual(id: string, make: string, model: string) {
    return this.call<Capture>('POST', `/v1/captures/${encodeURIComponent(id)}/resolve`, { make, model });
  }

  resolveUpload(id: string, pdf: Uint8Array | ArrayBuffer | Blob, filename: string, make?: string, model?: string) {
    const p = new URLSearchParams({ filename });
    if (make) p.set('make', make);
    if (model) p.set('model', model);
    return this.call<Capture>('POST', `/v1/captures/${encodeURIComponent(id)}/resolve?${p}`, pdf, 'application/pdf');
  }

  dismiss(id: string) {
    return this.call<Capture>('POST', `/v1/captures/${encodeURIComponent(id)}/resolve`, { dismiss: true });
  }

  cacheStats() {
    return this.call<Page<{ ns: string; entries: number; hits: number; costMicroUsd: number; savedMicroUsd: number }>>('GET', '/v1/cache/stats');
  }

  /** Every item in the library, page by page to `total` (never a single over-sized call). */
  async allItems(pageSize = 100): Promise<Item[]> {
    const out: Item[] = [];
    for (let offset = 0; ; ) {
      const page = await this.listItems(offset, pageSize);
      out.push(...page.items);
      if (out.length >= page.total || page.items.length === 0) {
        if (out.length < page.total) throw new Error(`item listing stopped at ${out.length} of ${page.total}`);
        return out;
      }
      offset += page.items.length;
    }
  }
}
